<template>
    <div class="lottery-time-settings">
        <!-- Main Table View -->
        <el-card class="settings-card">
            <template #header>
                <div class="card-header">
                    <span>开奖时间设置</span>
                    <div class="header-buttons">
                        <el-button type="warning" size="small" @click="openSyncDialog">
                            同步时间
                        </el-button>
                        <el-button type="primary" size="small" @click="getLotteryPeriods" :loading="loading">
                            刷新
                        </el-button>
                    </div>
                </div>
            </template>

            <el-table :data="lotteryPeriods" border stripe v-loading="loading">
                <el-table-column prop="lottery_name" label="彩票类型" width="120">
                    <template #default="scope">
                        {{ scope.row.lottery_name || getLotteryTypeName(scope.row.type) }}
                    </template>
                </el-table-column>
                <el-table-column label="下次开奖" width="180">
                    <template #default="scope">
                        <div class="next-draw-cell">
                            <div>{{ scope.row.next_draw_time || '-' }}</div>
                            <el-tag size="small" type="info">{{ scope.row.next_period }}</el-tag>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="倒计时" width="120">
                    <template #default="scope">
                        <span v-if="scope.row.status == 1" class="countdown-cell"
                            :class="{ 'countdown-urgent': scope.row.countdown_display === '开奖中...' }">
                            {{ scope.row.countdown_display || '--:--:--' }}
                        </span>
                        <span v-else class="countdown-paused">已暂停</span>
                    </template>
                </el-table-column>
                <el-table-column label="开奖间隔" width="100">
                    <template #default="scope">
                        {{ formatSeconds(scope.row.draw_interval) }}
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="100">
                    <template #default="scope">
                        <el-switch v-model="scope.row.status" :active-value="1" :inactive-value="0"
                            @change="toggleLotteryStatus(scope.row)" :loading="scope.row.statusLoading" />
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="280">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openUpdateTimeDialog(scope.row)">设置时间</el-button>
                        <el-button type="success" size="small" @click="openAutoScheduleDialog(scope.row)">排程</el-button>
                        <el-button type="warning" size="small" @click="openPreviewDialog(scope.row)">预览</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Update Time Dialog -->
        <el-dialog v-model="updateTimeDialogVisible" title="设置开奖时间" width="500px">
            <el-form :model="updateTimeForm" label-width="120px">
                <el-form-item label="彩票类型">
                    <el-input :value="currentLottery?.lottery_name || getLotteryTypeName(currentLottery?.type)"
                        disabled />
                </el-form-item>
                <el-form-item label="下次开奖时间">
                    <el-date-picker v-model="updateTimeForm.next_draw_time" type="datetime" placeholder="选择日期时间"
                        format="YYYY-MM-DD hh:mm A" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
                </el-form-item>
                <el-form-item label="开奖间隔">
                    <div class="interval-input">
                        <el-input-number v-model="intervalHours" :min="0" :max="168" />
                        <span>小时</span>
                        <el-input-number v-model="intervalMinutes" :min="0" :max="59" />
                        <span>分钟</span>
                    </div>
                    <div class="interval-presets">
                        <el-button size="small" @click="setIntervalPreset(1)">1分钟</el-button>
                        <el-button size="small" @click="setIntervalPreset(5)">5分钟</el-button>
                        <el-button size="small" @click="setIntervalPreset(10)">10分钟</el-button>
                        <el-button size="small" @click="setIntervalPreset(30)">30分钟</el-button>
                        <el-button size="small" @click="setIntervalPreset(60)">1小时</el-button>
                        <el-button size="small" @click="setIntervalPreset(1440)">1天</el-button>
                    </div>
                </el-form-item>
                <el-form-item label="状态">
                    <el-switch v-model="updateTimeForm.status" :active-value="1" :inactive-value="0" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="updateTimeDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitUpdateTime" :loading="submitLoading">确定</el-button>
            </template>
        </el-dialog>

        <!-- Auto Schedule Dialog -->
        <el-dialog v-model="autoScheduleDialogVisible" title="自动排程设置" width="550px">
            <el-form :model="autoScheduleForm" label-width="120px">
                <el-form-item label="彩票类型">
                    <el-input :value="currentLottery?.lottery_name || getLotteryTypeName(currentLottery?.type)"
                        disabled />
                </el-form-item>
                <el-form-item label="排程模式">
                    <el-radio-group v-model="scheduleMode">
                        <el-radio label="times">固定时间点</el-radio>
                        <el-radio label="interval">固定间隔</el-radio>
                    </el-radio-group>
                </el-form-item>

                <!-- Fixed Times Mode -->
                <el-form-item v-if="scheduleMode === 'times'" label="每日开奖时间">
                    <div class="draw-times-list">
                        <div v-for="(time, index) in autoScheduleForm.draw_times" :key="index" class="time-item">
                            <el-time-picker v-model="autoScheduleForm.draw_times[index]" format="hh:mm A"
                                value-format="HH:mm" placeholder="选择时间" />
                            <el-button type="danger" size="small" icon="Delete" circle @click="removeDrawTime(index)" />
                        </div>
                        <el-button type="primary" size="small" icon="Plus" @click="addDrawTime">添加时间</el-button>
                    </div>
                </el-form-item>

                <!-- Interval Mode -->
                <el-form-item v-if="scheduleMode === 'interval'" label="开奖间隔">
                    <div class="interval-input">
                        <el-input-number v-model="scheduleIntervalHours" :min="0" :max="168" />
                        <span>小时</span>
                        <el-input-number v-model="scheduleIntervalMinutes" :min="0" :max="59" />
                        <span>分钟</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="autoScheduleDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitAutoSchedule" :loading="submitLoading">确定</el-button>
            </template>
        </el-dialog>

        <!-- Preview Next Draws Dialog -->
        <el-dialog v-model="previewDialogVisible" title="预览未来开奖" width="500px">
            <div class="preview-header">
                <span>{{ currentLottery?.lottery_name || getLotteryTypeName(currentLottery?.type) }}</span>
                <span>未来7期开奖预览</span>
            </div>
            <el-table :data="previewDraws" border size="small">
                <el-table-column prop="period" label="期号" width="140" />
                <el-table-column prop="draw_time" label="开奖时间" />
            </el-table>
        </el-dialog>

        <!-- Sync Time Dialog -->
        <el-dialog v-model="syncDialogVisible" title="同步开奖时间" width="500px">
            <el-alert type="info" :closable="false" style="margin-bottom: 20px">
                将所有彩票设置为相同的开奖时间和间隔
            </el-alert>
            <el-form :model="syncForm" label-width="120px">
                <el-form-item label="下次开奖时间">
                    <el-date-picker v-model="syncForm.next_draw_time" type="datetime" placeholder="选择日期时间"
                        format="YYYY-MM-DD hh:mm A" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
                </el-form-item>
                <el-form-item label="开奖间隔">
                    <div class="interval-input">
                        <el-input-number v-model="syncHours" :min="0" :max="168" />
                        <span>小时</span>
                        <el-input-number v-model="syncMinutes" :min="0" :max="59" />
                        <span>分钟</span>
                    </div>
                    <div class="interval-presets">
                        <el-button size="small" @click="setSyncPreset(1)">1分钟</el-button>
                        <el-button size="small" @click="setSyncPreset(5)">5分钟</el-button>
                        <el-button size="small" @click="setSyncPreset(10)">10分钟</el-button>
                        <el-button size="small" @click="setSyncPreset(60)">1小时</el-button>
                        <el-button size="small" @click="setSyncPreset(1440)">1天</el-button>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="syncDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitSyncTime" :loading="submitLoading">同步所有</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, getCurrentInstance, onMounted, onUnmounted } from "vue";
import { ElMessage } from "element-plus";

const { proxy } = getCurrentInstance();

const loading = ref(false);
const submitLoading = ref(false);
const lotteryPeriods = ref([]);
const currentLottery = ref(null);
let countdownTimer = null;

// Update Time Dialog
const updateTimeDialogVisible = ref(false);
const updateTimeForm = ref({
    type: 0,
    next_draw_time: "",
    draw_interval: 60,
    status: 1
});
const intervalHours = ref(0);
const intervalMinutes = ref(1);

// Set interval preset (minutes)
const setIntervalPreset = (minutes) => {
    intervalHours.value = Math.floor(minutes / 60);
    intervalMinutes.value = minutes % 60;
};

// Sync Time Dialog
const syncDialogVisible = ref(false);
const syncForm = ref({
    next_draw_time: ""
});
const syncHours = ref(1);
const syncMinutes = ref(0);

const setSyncPreset = (minutes) => {
    syncHours.value = Math.floor(minutes / 60);
    syncMinutes.value = minutes % 60;
};

const openSyncDialog = () => {
    // Default to now + 1 hour
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    now.setSeconds(0);
    syncForm.value.next_draw_time = now.toISOString().slice(0, 19).replace('T', ' ');
    syncHours.value = 1;
    syncMinutes.value = 0;
    syncDialogVisible.value = true;
};

const submitSyncTime = async () => {
    const totalSeconds = (syncHours.value * 3600) + (syncMinutes.value * 60);
    if (totalSeconds < 60) {
        ElMessage.warning("间隔至少1分钟");
        return;
    }
    if (!syncForm.value.next_draw_time) {
        ElMessage.warning("请选择开奖时间");
        return;
    }
    
    submitLoading.value = true;
    try {
        // Update all lotteries
        const promises = lotteryPeriods.value.map(period => 
            proxy.$http({
                method: "post",
                url: "/LotteryDraw/updateLotteryTime",
                data: {
                    type: period.type,
                    next_draw_time: syncForm.value.next_draw_time,
                    draw_interval: totalSeconds,
                    status: 1
                }
            })
        );
        
        const results = await Promise.all(promises);
        const allSuccess = results.every(res => res.code == 0);
        
        if (allSuccess) {
            ElMessage.success("同步成功");
            syncDialogVisible.value = false;
            setTimeout(() => {
                getLotteryPeriods();
            }, 300);
        } else {
            ElMessage.error("部分同步失败");
        }
    } catch (error) {
        ElMessage.error("同步失败");
    } finally {
        submitLoading.value = false;
    }
};

// Auto Schedule Dialog
const autoScheduleDialogVisible = ref(false);
const scheduleMode = ref("times");
const autoScheduleForm = ref({
    type: 0,
    draw_times: ["09:30", "12:00", "21:30"],
    interval: 0
});
const scheduleIntervalHours = ref(0);
const scheduleIntervalMinutes = ref(5);

// Preview Dialog
const previewDialogVisible = ref(false);
const previewDraws = ref([]);

// Format seconds to readable string
const formatSeconds = (seconds) => {
    if (!seconds) return "-";
    if (seconds >= 86400) {
        const days = Math.floor(seconds / 86400);
        return `${days}天`;
    }
    if (seconds >= 3600) {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return mins > 0 ? `${hours}小时${mins}分` : `${hours}小时`;
    }
    if (seconds >= 60) {
        const mins = Math.floor(seconds / 60);
        return `${mins}分钟`;
    }
    return `${seconds}秒`;
};

// Get lottery type name
const getLotteryTypeName = (type) => {
    const names = { 0: '澳彩', 1: '港彩' };
    return names[type] || `类型${type}`;
};

// Format countdown display
const formatCountdown = (seconds) => {
    if (seconds <= 0) return "开奖中...";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Update countdown every second
const updateCountdowns = () => {
    lotteryPeriods.value.forEach(period => {
        if (period.next_draw_time) {
            const drawTime = new Date(period.next_draw_time).getTime();
            const now = Date.now();
            const diff = Math.floor((drawTime - now) / 1000);
            period.countdown_display = formatCountdown(diff);
        }
    });
};

// Get all lottery periods
const getLotteryPeriods = async () => {
    loading.value = true;
    try {
        const res = await proxy.$http({
            type: "get",
            url: "/LotteryDraw/getLotteryPeriods"
        });
        if (res.code == 0) {
            lotteryPeriods.value = res.data?.list || res.data || [];
            updateCountdowns();
        }
    } catch (error) {
        console.error("Failed to get lottery periods:", error);
    } finally {
        loading.value = false;
    }
};

// Toggle lottery status (enable/disable)
const toggleLotteryStatus = async (period) => {
    period.statusLoading = true;
    try {
        const res = await proxy.$http({
            method: "post",
            url: "/LotteryDraw/updateLotteryTime",
            data: {
                type: period.type,
                status: period.status
            }
        });
        if (res.code == 0) {
            ElMessage.success(period.status == 1 ? "已启用" : "已禁用");
        } else {
            // Revert on failure
            period.status = period.status == 1 ? 0 : 1;
            ElMessage.error(res.msg || "操作失败");
        }
    } catch (error) {
        // Revert on error
        period.status = period.status == 1 ? 0 : 1;
        ElMessage.error("操作失败");
    } finally {
        period.statusLoading = false;
    }
};

// Open Update Time Dialog
const openUpdateTimeDialog = (period) => {
    currentLottery.value = period;
    const totalSeconds = period.draw_interval || 60;
    intervalHours.value = Math.floor(totalSeconds / 3600);
    intervalMinutes.value = Math.floor((totalSeconds % 3600) / 60);
    updateTimeForm.value = {
        type: period.type,
        next_draw_time: period.next_draw_time || "",
        draw_interval: totalSeconds,
        status: period.status || 1
    };
    updateTimeDialogVisible.value = true;
};

// Submit Update Time
const submitUpdateTime = async () => {
    const totalSeconds = (intervalHours.value * 3600) + (intervalMinutes.value * 60);
    if (totalSeconds < 60) {
        ElMessage.warning("间隔至少1分钟");
        return;
    }
    submitLoading.value = true;
    try {
        const res = await proxy.$http({
            method: "post",
            url: "/LotteryDraw/updateLotteryTime",
            data: { ...updateTimeForm.value, draw_interval: totalSeconds }
        });
        if (res.code == 0) {
            ElMessage.success("设置成功");
            updateTimeDialogVisible.value = false;
            // Small delay to ensure backend has saved
            setTimeout(() => {
                getLotteryPeriods();
            }, 300);
        } else {
            ElMessage.error(res.msg || "设置失败");
        }
    } catch (error) {
        ElMessage.error("设置失败");
    } finally {
        submitLoading.value = false;
    }
};

// Open Auto Schedule Dialog
const openAutoScheduleDialog = (period) => {
    currentLottery.value = period;
    autoScheduleForm.value = {
        type: period.type,
        draw_times: ["09:30", "12:00", "21:30"],
        interval: 0
    };
    scheduleMode.value = "times";
    autoScheduleDialogVisible.value = true;
};

// Add/Remove draw times
const addDrawTime = () => {
    autoScheduleForm.value.draw_times.push("12:00");
};
const removeDrawTime = (index) => {
    autoScheduleForm.value.draw_times.splice(index, 1);
};

// Submit Auto Schedule
const submitAutoSchedule = async () => {
    submitLoading.value = true;
    try {
        const totalSeconds = (scheduleIntervalHours.value * 3600) + (scheduleIntervalMinutes.value * 60);
        const data = {
            type: autoScheduleForm.value.type,
            draw_times: scheduleMode.value === "times" ? autoScheduleForm.value.draw_times : [],
            interval: scheduleMode.value === "interval" ? totalSeconds : 0
        };
        const res = await proxy.$http({
            method: "post",
            url: "/LotteryDraw/setAutoSchedule",
            data
        });
        if (res.code == 0) {
            ElMessage.success("排程设置成功");
            autoScheduleDialogVisible.value = false;
            getLotteryPeriods();
        } else {
            ElMessage.error(res.msg || "设置失败");
        }
    } catch (error) {
        ElMessage.error("设置失败");
    } finally {
        submitLoading.value = false;
    }
};

// Open Preview Dialog
const openPreviewDialog = (period) => {
    currentLottery.value = period;
    // Generate preview of next 7 draws
    const draws = [];
    let currentTime = new Date(period.next_draw_time);
    let currentPeriod = period.next_period;

    for (let i = 0; i < 7; i++) {
        draws.push({
            period: currentPeriod,
            draw_time: currentTime.toLocaleString('zh-CN', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hour12: true
            })
        });
        // Add interval for next draw
        currentTime = new Date(currentTime.getTime() + (period.draw_interval * 1000));
        // Increment period number
        const periodNum = parseInt(currentPeriod.slice(-3)) + 1;
        currentPeriod = currentPeriod.slice(0, -3) + String(periodNum).padStart(3, '0');
    }
    previewDraws.value = draws;
    previewDialogVisible.value = true;
};

onMounted(() => {
    getLotteryPeriods();
    // Update countdown every second
    countdownTimer = setInterval(updateCountdowns, 1000);
});

onUnmounted(() => {
    if (countdownTimer) {
        clearInterval(countdownTimer);
    }
});
</script>

<style lang="less" scoped>
.lottery-time-settings {
    padding: 0;
}

.settings-card {
    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .header-buttons {
            display: flex;
            gap: 10px;
        }
    }
}

.next-draw-cell {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.countdown-cell {
    font-family: monospace;
    font-size: 16px;
    font-weight: bold;
    color: #409eff;
}

.countdown-urgent {
    color: #e6a23c;
    animation: blink 1s infinite;
}

@keyframes blink {
    50% {
        opacity: 0.5;
    }
}

.countdown-paused {
    font-size: 14px;
    color: #909399;
    font-style: italic;
}

.interval-input {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;

    span {
        color: #666;
    }
}

.interval-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.draw-times-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .time-item {
        display: flex;
        align-items: center;
        gap: 10px;
    }
}

.preview-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
    font-weight: bold;
    color: #333;
}
</style>
